import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAssistant,
  updateAssistant,
  fetchAllAssistants,
  createAssistant,
  deleteAssistant,
  updateAssistantKnowledgeBase,
  updateAssistantSettings,
  updateAssistantNumber,
  startComposioAuth,
} from "./assistantAPI";

export const getAssistant = createAsyncThunk(
  "assistant/getAssistant",
  async (uuid, thunkAPI) => {
    try {
      const data = await fetchAssistant(uuid);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAllAssistants = createAsyncThunk(
  "assistant/getAllAssistants",
  async (accountId, thunkAPI) => {
    try {
      const data = await fetchAllAssistants(accountId);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const addAssistant = createAsyncThunk(
  "assistant/addAssistant",
  async (assistantData, thunkAPI) => {
    try {
      const data = await createAssistant(assistantData);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editAssistant = createAsyncThunk(
  "assistant/editAssistant",
  async ({ uuid, data }, thunkAPI) => {
    try {
      const updated = await updateAssistant(uuid, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editAssistantKnowledgeBase = createAsyncThunk(
  "assistant/editAssistantKnowledgeBase",
  async ({ uuid, data }, thunkAPI) => {
    try {
      const updated = await updateAssistantKnowledgeBase(uuid, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editAssistantNumber = createAsyncThunk(
  "assistant/editAssistantNumber",
  async ({ uuid, data }, thunkAPI) => {
    try {
      const updated = await updateAssistantNumber(uuid, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const editAssistantSettings = createAsyncThunk(
  "assistant/editAssistantSettings",
  async ({ uuid, data }, thunkAPI) => {
    try {
      const updated = await updateAssistantSettings(uuid, data);
      return updated;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const removeAssistant = createAsyncThunk(
  "assistant/removeAssistant",
  async (uuid, thunkAPI) => {
    try {
      const data = await deleteAssistant(uuid);
      return { uuid, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const initiateComposioAuth = createAsyncThunk(
  "assistant/initiateComposioAuth",
  async (payload, thunkAPI) => {
    try {
      const data = await startComposioAuth(payload);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

const assistantSlice = createSlice({
  name: "assistant",
  initialState: {
    assistants: [],
    selectedAssistant: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearSelectedAssistant(state) {
      state.selectedAssistant = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearAssistants: (state) => {
      state.assistants = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAssistant.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAssistant = action.payload;
      })
      .addCase(getAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getAllAssistants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAssistants.fulfilled, (state, action) => {
        state.loading = false;
        state.assistants = action.payload;
      })
      .addCase(getAllAssistants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAssistant.fulfilled, (state, action) => {
        state.loading = false;
        state.assistants.push(action.payload);
      })
      .addCase(addAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAssistant.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.assistants.findIndex(
          (a) => a.uuid === action.payload.uuid,
        );
        if (idx !== -1) state.assistants[idx] = action.payload;
        if (state.selectedAssistant?.uuid === action.payload.uuid) {
          state.selectedAssistant = action.payload;
        }
      })
      .addCase(editAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editAssistantKnowledgeBase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAssistantKnowledgeBase.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.assistants.findIndex(
          (a) => a.uuid === action.payload.uuid,
        );
        if (idx !== -1) state.assistants[idx] = action.payload;
        if (state.selectedAssistant?.uuid === action.payload.uuid) {
          state.selectedAssistant = action.payload;
        }
      })
      .addCase(editAssistantKnowledgeBase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(editAssistantSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAssistantSettings.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.assistants.findIndex(
          (a) => a.uuid === action.payload.uuid,
        );
        if (idx !== -1) state.assistants[idx] = action.payload;
        if (state.selectedAssistant?.uuid === action.payload.uuid) {
          state.selectedAssistant = action.payload;
        }
      })
      .addCase(editAssistantSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(removeAssistant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAssistant.fulfilled, (state, action) => {
        state.loading = false;
        state.assistants = state.assistants.filter(
          (a) => a.uuid !== action.payload.uuid,
        );
        if (state.selectedAssistant?.uuid === action.payload.uuid) {
          state.selectedAssistant = null;
        }
      })
      .addCase(removeAssistant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(initiateComposioAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiateComposioAuth.fulfilled, (state, action) => {
        state.loading = false;
        // No assistant state update needed here unless backend returns updated assistant
      })
      .addCase(initiateComposioAuth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedAssistant, clearError, clearAssistants } =
  assistantSlice.actions;

export default assistantSlice.reducer;
